import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { policiesService } from '../services/policies.service';
import {
  DashboardPoliciesResponse,
  DashboardPolicy,
  PolicyType,
} from '../components/policies/types';
import {
  DeploymentStatus,
  TrackedDeployment,
  TERMINAL_DEPLOYMENT_STATUSES,
  DEPLOYMENT_TRACKING_STORAGE_KEY,
} from '../services/types';
import type { RootState } from '../store';

/**
 * Read active tracked deployments from localStorage and re-apply
 * "InProgress" status to freshly fetched policies so the UI doesn't
 * flash back to "Not Available" on page refresh.
 */
function rehydrateInProgressStatuses(policies: DashboardPolicy[]): void {
  try {
    const raw = localStorage.getItem(DEPLOYMENT_TRACKING_STORAGE_KEY);
    if (!raw) return;
    const items: TrackedDeployment[] = JSON.parse(raw);
    // Build a set of active "entityId::tenantId" keys for fast lookup
    const activeKeys = new Set<string>();
    items.forEach(item => {
      if (!TERMINAL_DEPLOYMENT_STATUSES.has(item.apiStatus) && item.view === 'dashboard') {
        activeKeys.add(`${item.entityId.toLowerCase()}::${item.tenantId.toLowerCase()}`);
      }
    });
    if (activeKeys.size === 0) return;
    // Stamp matching tenant deployments as InProgress
    policies.forEach(policy => {
      policy.tenantDeployments.forEach(td => {
        const key = `${policy.policyName.toLowerCase()}::${td.tenantId.toLowerCase()}`;
        if (activeKeys.has(key)) {
          td.status = 'InProgress';
        }
      });
    });
  } catch {
    // Silently ignore — worst case the status stays as-is until the next poll
  }
}

// ========== State Interface ==========

interface DashboardPoliciesState {
  // Data
  policies: DashboardPolicy[];
  baseTenantId: string;
  baseTenantName: string;
  totalAuthorizedTenants: number;
  
  // Pagination (client-side)
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;

  // Per-type totals (drive tab badges; independent of policyType filter)
  dlpTotalCount: number;
  autoLabelTotalCount: number;
  
  // Loading & Error states
  loading: boolean;
  error: string | null;
  
  // Metadata
  lastFetched: string | null;
  /** requestId of the most recently dispatched fetch; older responses are discarded. */
  latestRequestId: string | null;
}

const initialState: DashboardPoliciesState = {
  policies: [],
  baseTenantId: '',
  baseTenantName: '',
  totalAuthorizedTenants: 0,
  pageNumber: 1,
  pageSize: 25,
  totalCount: 0,
  totalPages: 0,
  hasNextPage: false,
  dlpTotalCount: 0,
  autoLabelTotalCount: 0,
  loading: false,
  error: null,
  lastFetched: null,
  latestRequestId: null,
};

// ========== Async Thunks ==========

/**
 * Fetch all dashboard policies (DLP + AutoLabel combined)
 * Client-side filtering will be applied in components
 */
export const fetchDashboardPolicies = createAsyncThunk<
  DashboardPoliciesResponse,
  { searchTerm?: string; pageNumber?: number; pageSize?: number; policyType?: PolicyType } | void,
  { rejectValue: string }
>(
  'dashboardPolicies/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const searchTerm = params?.searchTerm;
      const pageNumber = params?.pageNumber || 1;
      const pageSize = params?.pageSize || 25;
      const policyType = params?.policyType;
      
      const response = await policiesService.fetchDashboardPolicies(
        searchTerm,
        pageNumber,
        pageSize,
        policyType,
      );
      
      // Re-apply InProgress statuses from localStorage before returning
      // (side effect: reads localStorage — must run in thunk, not reducer)
      rehydrateInProgressStatuses(response.policies);
      
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to fetch dashboard policies';
      return rejectWithValue(errorMessage);
    }
  }
);

// ========== Slice ==========

const dashboardPoliciesSlice = createSlice({
  name: 'dashboardPolicies',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear all data
    clearDashboardPolicies: (state) => {
      return initialState;
    },
    
    // Update page number (for client-side pagination)
    setPageNumber: (state, action: PayloadAction<number>) => {
      state.pageNumber = Math.max(1, action.payload);
    },
    
    // Update page size
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = Math.max(1, action.payload);
      state.pageNumber = 1; // Reset to first page when page size changes
    },
    
    /**
     * Update tenant deployment statuses after a batch deploy
     * Payload: Map serialized as array of [key, status] where key = "entityId::tenantId"
     */
    updateTenantDeploymentStatuses: (
      state,
      action: PayloadAction<Array<[string, DeploymentStatus]>>
    ) => {
      const updates = new Map(action.payload);
      state.policies.forEach(policy => {
        policy.tenantDeployments.forEach(td => {
          // Key matches entityId (policyName) used in deployment API requests
          const key = `${policy.policyName.toLowerCase()}::${td.tenantId.toLowerCase()}`;
          const newStatus = updates.get(key);
          if (newStatus) {
            td.status = newStatus;
          }
        });
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard policies
      //
      // `latestRequestId` discards superseded responses. Tab / page / search changes and the
      // summary-step refresh can be in flight at the same time; without this guard an older
      // request that resolves last overwrites the fresher deployment status the summary step
      // just fetched, and the summary silently renders stale data.
      .addCase(fetchDashboardPolicies.pending, (state, action) => {
        state.latestRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardPolicies.fulfilled, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.policies = action.payload.policies;
        state.baseTenantId = action.payload.baseTenantId;
        state.baseTenantName = action.payload.baseTenantName;
        state.totalAuthorizedTenants = action.payload.totalAuthorizedTenants;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.hasNextPage = action.payload.hasNextPage;
        // Only overwrite per-type totals if server provided them; otherwise keep
        // the previously-known values so switching tabs doesn't drop the badge.
        if (typeof action.payload.dlpTotalCount === 'number') {
          state.dlpTotalCount = action.payload.dlpTotalCount;
        }
        if (typeof action.payload.autoLabelTotalCount === 'number') {
          state.autoLabelTotalCount = action.payload.autoLabelTotalCount;
        }
        state.lastFetched = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchDashboardPolicies.rejected, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload || 'Failed to fetch dashboard policies';
      });
  },
});

// ========== Actions ==========

export const {
  clearError,
  clearDashboardPolicies,
  setPageNumber,
  setPageSize,
  updateTenantDeploymentStatuses,
} = dashboardPoliciesSlice.actions;

// ========== Selectors ==========

export const selectDashboardPolicies = (state: RootState) =>
  state.dashboardPolicies.policies.filter(p => p.rules.length > 0);
export const selectDashboardPoliciesLoading = (state: RootState) => state.dashboardPolicies.loading;
export const selectDashboardPoliciesError = (state: RootState) => state.dashboardPolicies.error;
export const selectDashboardPoliciesMetadata = (state: RootState) => ({
  baseTenantId: state.dashboardPolicies.baseTenantId,
  baseTenantName: state.dashboardPolicies.baseTenantName,
  totalAuthorizedTenants: state.dashboardPolicies.totalAuthorizedTenants,
  pageNumber: state.dashboardPolicies.pageNumber,
  pageSize: state.dashboardPolicies.pageSize,
  totalCount: state.dashboardPolicies.totalCount,
  totalPages: state.dashboardPolicies.totalPages,
  hasNextPage: state.dashboardPolicies.hasNextPage,
  dlpTotalCount: state.dashboardPolicies.dlpTotalCount,
  autoLabelTotalCount: state.dashboardPolicies.autoLabelTotalCount,
  lastFetched: state.dashboardPolicies.lastFetched,
});

// Selector to get DLP policies only
export const selectDLPPolicies = (state: RootState) =>
  state.dashboardPolicies.policies.filter(p => p.policyType === 'DlpCompliancePolicy' && p.rules.length > 0);

// Selector to get AutoLabel policies only
export const selectAutoLabelPolicies = (state: RootState) =>
  state.dashboardPolicies.policies.filter(p => p.policyType === 'AutoSensitivityLabelPolicy' && p.rules.length > 0);

// Selector to get policy by ID
export const selectDashboardPolicyById = (policyId: string) => (state: RootState) =>
  state.dashboardPolicies.policies.find(p => p.policyId === policyId);

// ========== Export Reducer ==========

export default dashboardPoliciesSlice.reducer;
